import React, { FC, useContext, useState } from 'react';
import { TShortAccount, TShortAccountSelected } from './types';
import { DivWrapper, AccordionCardHeader, SpanTitle, DivSubAccount, AccordionCard, AccordionCardTitle } from './styles';
import Accordion from 'react-bootstrap/Accordion';
import { AccordionContext, useAccordionToggle } from 'react-bootstrap';
import FnCheckbox from '../../components/elements/FnCheckbox/index';
import { addSelectKey, filterSelectedAccounts, selectAllSubAccounts } from './utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

type TProps = {
  data: TShortAccount[];
  selectedData: TShortAccount[];
  onChange: (data: TShortAccount[]) => void;
};

export const AccountsUserManagement: FC<TProps> = ({ data, selectedData, onChange }) => {
  const ContextAwareToggle = ({
    children,
    eventKey,
    callback
  }: {
    children: JSX.Element;
    eventKey: string;
    callback?: (key: string) => void;
  }) => {
    const currentEventKey = useContext(AccordionContext);
    const decoratedOnClick = useAccordionToggle(eventKey, () => callback && callback(eventKey));
    const isCurrentEventKey = currentEventKey === eventKey;

    return (
      <Accordion.Toggle
        eventKey={eventKey}
        as={AccordionCardTitle}
        onClick={decoratedOnClick}
        className={clsx({ ['is-collapsed']: isCurrentEventKey }, 'user-management-accordion-card-title')}
      >
        {children}
        <FontAwesomeIcon icon={faChevronDown} />
      </Accordion.Toggle>
    );
  };

  const formattedData: TShortAccountSelected[] = Array.isArray(data) ? addSelectKey(data, selectedData) : [];
  const [currentData, setCurrentData] = useState(formattedData);

  const handleChange = (id, value, accountType?) => {
    const newData = accountType
      ? currentData.map((account) => {
          return account.id === id
            ? { ...account, selected: value, subAccounts: selectAllSubAccounts(account.subAccounts, value) }
            : account;
        })
      : currentData.map((account) => {
          let isCurrentAccount = false;
          const newSubAccounts = Array.isArray(account.subAccounts)
            ? account.subAccounts.map((subAccount) => {
                if (subAccount.id === id) {
                  isCurrentAccount = true;
                  return { ...subAccount, selected: value };
                }
                return subAccount;
              })
            : null;

          const isAccountSelected = isCurrentAccount
            ? Array.isArray(newSubAccounts) && !!newSubAccounts.filter((subAccount) => subAccount.selected).length
            : account.selected;

          return { ...account, selected: isAccountSelected, subAccounts: newSubAccounts };
        });
    setCurrentData([...newData]);
    onChange(filterSelectedAccounts(newData));
  };

  return (
    <DivWrapper>
      <Accordion defaultActiveKey="0">
        {currentData.map((shortAccount: TShortAccountSelected) => (
          <AccordionCard key={shortAccount.id} className="user-management-accordion-card">
            <AccordionCardHeader className="user-management-accordion-card-header">
              <ContextAwareToggle eventKey={shortAccount.id}>
                <>
                  <FnCheckbox
                    name={shortAccount.id}
                    value={shortAccount.selected}
                    onChange={() => handleChange(shortAccount.id, !shortAccount.selected, true)}
                  />
                  <SpanTitle>{shortAccount.accountName}</SpanTitle>
                </>
              </ContextAwareToggle>
            </AccordionCardHeader>
            <Accordion.Collapse eventKey={shortAccount.id}>
              <div>
                {Array.isArray(shortAccount.subAccounts) &&
                  shortAccount.subAccounts.map((shortSubAccount: TShortAccountSelected) => (
                    <DivSubAccount key={shortSubAccount.id}>
                      <FnCheckbox
                        name={shortSubAccount.id}
                        value={shortSubAccount.selected}
                        label={shortSubAccount.accountName}
                        onChange={() => handleChange(shortSubAccount.id, !shortSubAccount.selected)}
                      />
                    </DivSubAccount>
                  ))}
              </div>
            </Accordion.Collapse>
          </AccordionCard>
        ))}
      </Accordion>
    </DivWrapper>
  );
};
